import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type { Mutation, SaveUserAccountMutationInput } from '@/generated/graphql';
import * as UserAccount from '@/types/UserAccount';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

const USER_DETAILS_FRAGMENT = gql`
  fragment UserDetails on UserAccount {
    id
    user {
      id
      name
      email
    }
    teamRole {
      id
      name
    }
  }
`;
const ALL_USER_ACCOUNTS_QUERY = gql`
  query allUserAccounts($accountId: Int!, $filter: JSON) {
    app {
      userAccounts(accountId: $accountId, filter: $filter) {
        collection {
          ...UserDetails
        }
      }
    }
  }
  ${USER_DETAILS_FRAGMENT}
`;

const SAVE_USER_ACCOUNT_MUTATION = gql`
  mutation SaveUserAccountMutation($input: SaveUserAccountMutationInput!) {
    app {
      saveUserAccount(input: $input) {
        clientMutationId
        errors
        userAccount {
          ...UserDetails
        }
      }
    }
  }
  ${USER_DETAILS_FRAGMENT}
`;
interface UserAccountState {
  usersAccounts: UserAccount.UserAccount[];
  userAccountLoading: boolean;
}

export const useUserAccountsStore = defineStore('userAccount', {
  state: (): UserAccountState => ({
    usersAccounts: [],
    userAccountLoading: false,
  }),

  actions: {
    async fetchAllUsersAccounts(accountId: number, userId?: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.userAccountLoading = true;

      try {
        const filter = userId ? { userId } : null;
        const variables = { accountId, filter };

        const { data, errors } = await apiClient.query({
          query: ALL_USER_ACCOUNTS_QUERY,
          variables,
          fetchPolicy: 'network-only',
        });

        if (errors) {
          console.error('Error fetching user accounts:', errors);
          return;
        }

        this.usersAccounts = (data.app?.userAccounts.collection || []).map(UserAccount.fromGql);
      } finally {
        this.userAccountLoading = false;
      }
    },

    async saveUser(
      payload: SaveUserAccountMutationInput,
      userId?: number,
    ): Promise<RequestResult<UserAccount.UserAccount>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.userAccountLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_USER_ACCOUNT_MUTATION,
          variables: { input: { ...payload, userId } },
        });

        if (data?.app?.saveUserAccount?.errors) {
          return { errors: data?.app.saveUserAccount.errors };
        }

        if (data?.app?.saveUserAccount?.userAccount) {
          return { result: UserAccount.fromGql(data?.app?.saveUserAccount?.userAccount) };
        }

        return {};
      } finally {
        this.userAccountLoading = false;
      }
    },
  },
});
